import { BasicEvent } from "@h4x/common";
import type { Subscriber, Unsubscriber } from "svelte/store";
import { HewSync } from "./HewSync";
import type { HewSyncType } from "./HewSyncType";

class HewSyncListData<K extends typeof HewSyncType, T extends HewSyncType> {
	public readonly onUpdate = new BasicEvent<() => void>();
	public readonly _items: T[] = [];

	public readonly loading: boolean = true;
	public readonly updating = false;
	public readonly errors: Error[] = [];

	private nextToken?: string | false;

	public readonly type: K & InternalType<T>;
	public constructor(type: K, private filter?: (data: T) => boolean) {
		this.type = type as unknown as K & InternalType<T>;
	}

	private initializeSubscription() {
		// eslint-disable-next-line @typescript-eslint/unbound-method
		this.type.onSubscriptionEvent.addCallback(this.handleSubscriptionEvent, this);
	}

	private handleSubscriptionEvent(data: T & { removed?: boolean }) {
		console.log("subscribe", data);
		let key = this.type.getKeyFromData(data);
		let found = false;
		for (const item of this._items) {
			let key2 = this.type.getKeyFromData(item);
			if (key2 === key) {
				if (data.removed) {
					console.log("Removing", item);
					this._items.splice(this._items.indexOf(item), 1);
				}
				found = true;
				break;
			}
		}
		if (!found) {
			let item = this.type.cache.get(key);
			if (item === undefined) {
				item = this.type.from(data);
				(item as unknown as { setLoaded: () => void }).setLoaded();
				this.type.cache.set(key, item);
			}
			if (item.loading === true) {
				(item as unknown as { apply: (data: any) => void }).apply(data);
				(item as unknown as { setLoaded: () => void }).setLoaded();
			}
			this._items.push(item);
		}
		this.onUpdate.execute();
	}

	private loadInitialized = false;
	public async initialLoad() {
		if (this.loadInitialized === false) {
			this.loadInitialized = true;
			return this.fetchMore();
		}
		return this.nextToken !== false;
	}

	private subscriptionInitialized = false;
	private fetchMorePromise?: Promise<boolean>;
	public async fetchMore() {
		if (this.fetchMorePromise !== undefined) {
			return await this.fetchMorePromise;
		}

		this.fetchMorePromise = this.fetchMoreInternal();
		await this.fetchMorePromise;
		return this.nextToken !== false;
	}

	private async fetchMoreInternal() {
		if (this.nextToken === false) {
			return false;
		}
		this.loadInitialized = true;
		if (this.subscriptionInitialized === false) {
			this.subscriptionInitialized = true;
			this.initializeSubscription();
		}
		let inputs = [];
		if (this.nextToken !== undefined) {
			inputs.push(new HewSync.QueryVariable("nextToken", "String", this.nextToken));
		}
		let query = HewSync.queryList<K, T>(this.type, inputs);
		let result = await query.execute();
		console.log(this.type.name, "Fetched items", result);
		const items = result.items.map((x) => {
			let key = this.type.getKeyFromData(x);
			let cached = this.type.cache.get(key);
			if (cached !== undefined) {
				if (cached.loading === true) {
					(cached as unknown as { apply: (data: any) => void }).apply(x);
					(cached as unknown as { setLoaded: () => void }).setLoaded();
				}
				return cached;
			}
			cached = this.type.from(x);
			(cached as unknown as { setLoaded: () => void }).setLoaded();
			this.type.cache.set(key, cached);
			return cached;
		});
		this._items.push(...items);
		this.nextToken = result.nextToken ?? false;
		this.setLoaded();
		this.fetchMorePromise = undefined;

		this.onUpdate.execute();

		return this.nextToken !== false;
	}

	private fetchingAllPromise?: Promise<void>;
	public async fetchAll() {
		if (this.fetchingAllPromise !== undefined) {
			return await this.fetchingAllPromise;
		}

		this.fetchingAllPromise = this.fetchAllInternal();
		await this.fetchingAllPromise;
	}

	private async fetchAllInternal() {
		while (await this.fetchMore()) {}
	}

	public get hasMore() {
		return this.nextToken !== false;
	}

	protected setLoaded() {
		(this.loading as any) = false;
	}

	public setUpdating(value: boolean) {
		(this.updating as any) = value;
		this.onUpdate.execute();
	}
}

type InternalType<T> = {
	getKeyFromData(data: T): string;
	from(data: any): T;
	apply(data: any);
	cache: Map<string, T>;
	onSubscriptionEvent: BasicEvent<(data: any) => void>;
	initSubscriptions: () => void;
};

export class HewSyncList<K extends typeof HewSyncType, T extends HewSyncType> {
	public readonly onUpdate = new BasicEvent<() => void>();

	private readonly store: HewSyncListData<K, T>;

	public get _items() {
		return this.store._items;
	}

	public get loading() {
		return this.store.loading;
	}

	public get updating() {
		return this.store.updating;
	}

	public get errors() {
		return this.store.errors;
	}

	public get items() {
		return this._items.filter((x) => this.filter?.(x) ?? true);
	}

	private static cache = new WeakMap<typeof HewSyncType, HewSyncListData<any, any>>();
	public readonly type: K & InternalType<T>;
	private constructor(type: K, private filter?: (data: T) => boolean) {
		this.type = type as unknown as K & InternalType<T>;

		this.type.initSubscriptions();

		let store = HewSyncList.cache.get(type);
		if (store === undefined) {
			store = new HewSyncListData(type, filter);
			HewSyncList.cache.set(type, store);
		}
		this.store = store;

		this.store.onUpdate.addCallback(this.onUpdate.execute, this.onUpdate);
	}

	public async initialLoad() {
		return this.store.initialLoad();
	}

	public async fetchMore() {
		return this.store.fetchMore();
	}

	public async fetchAll() {
		return this.store.fetchAll();
	}

	public get hasMore() {
		return this.store.hasMore;
	}

	public setUpdating(value: boolean) {
		this.store.setUpdating(value);
	}

	public subscribe(run: Subscriber<this>): Unsubscriber {
		let ref = this.onUpdate.addCallback(() => {
			run(this);
		});
		run(this);
		return () => {
			ref.remove();
		};
	}
}
